<template lang="pug">
#other-user-template-confirm
  // title
  .d-flex.flex-row.justify-content-center.mb-2
    .page-title 安否確認（施設関係者）の事前設定

  // progress
  ProcessIndicator(v-model="indicator")
  .mb-3.separator

  // warning messages
  template
    .mb-3
      span.error-message {{ errorMessage }}

  // group name
  .mb-0
    span.key-name 宛先（宛先グループ）
  #group-name.mb-3
    .mb-0
      span(v-if="_groupName != ''") {{ _groupName }}
      span.error-message(v-else) 未入力
  // openingSentence
  .mb-0
    span.key-name メッセージ内容
  #opening-sentence.mb-3
    .mb-0
      span(v-if="openingSentence != ''") {{ openingSentence }}
      span.error-message(v-else) 未入力

  // questions
  .mt-2.mb-0
    span.key-name 質問項目
  #questions.mb-3
    .mb-0
      span {{ createQuestionsLabel(confirms) }}

  // buttons
  .d-flex.flex-row.justify-content-center
    b-button.btn.register-btn.mr-2(@click="onUpdate") 更新
    b-button.btn.back-btn(@click="onCancel") 戻る

  // loading component
  Loading(:isBusy="isBusy", :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore, mobileSafetyConfirmOtherUserTemplateStore } from "@/store/store";
import { MobileSafetyConfirmOtherUserTemplateState } from "@/store/MobileSafetyConfirmOtherUserTemplate";
import { MobileUrl } from "@/commons/constants/MobileUrl";
import SystemError from "@/commons/models/SystemError";

import OtherUserSafetyConfirmsTemplatesService, { OtherUserSafetyConfirmsTemplatesPatchModel } from "@/commons/services/OtherUserSafetyConfirmsTemplates";

import Loading from "@/mobile/basics/Loading.vue";
import ProcessIndicator, { ProcessIndicatorModel } from "@/mobile/basics/ProcessIndicator.vue";
import { UseId } from "@/mobile/components/SafetyConfirmOtherUserQuestions.vue";
import {
  SafetyConfirmTargetMessageGroupModalModel,
  SafetyConfirmTargetMessageGroupModel,
  SafetyConfirmTargetOtherUserGroupModel,
  TargetType
} from "@/mobile/components/modals/SafetyConfirmTargetMessageGroupModal.vue";

/**
 * ### 安否確認 事前設定(施設関係者) 確認コンポーネント
 */
@Component({
  components: {
    Loading,
    ProcessIndicator
  }
})
export default class OtherUserTemplateConfirm extends Vue {
  /** 安否確認(施設関係者)テンプレートサービス */
  @Inject("OtherUserSafetyConfirmsTemplatesService") otherUserSafetyConfirmsTemplatesService!: OtherUserSafetyConfirmsTemplatesService;

  /** ### 安否確認事前設定(施設関係者)画面での入力情報 */
  @Getter("mobileSafetyConfirmOtherUserTemplateState") state!: MobileSafetyConfirmOtherUserTemplateState;

  /** インジケータ */
  indicator: Array<ProcessIndicatorModel> = [
    { text: "入力", current: false },
    { text: "確認", current: true },
    { text: "完了", current: false }
  ];

  /** 読み込み中モーダル表示可否 */
  isBusy: boolean = false;
  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "読込み中";

  /** 宛先グループ */
  groupTargets: Array<SafetyConfirmTargetMessageGroupModalModel> = [];
  /** 冒頭文 */
  openingSentence: string = "";
  /** 質問リスト */
  confirms: Array<UseId> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  get _groupName(): string {
    if (!this.groupTargets || this.groupTargets.length < 1) {
      return "";
    }

    const names: string[] = [];
    for (const groupTarget of this.groupTargets) {
      for (const target of groupTarget.targets) {
        if (target.isTarget) {
          names.push(target.name);
        }
      }
    }
    return names.join("、");
  }

  /**
   * ### vue lifecycle mounted
   */
  public mounted(): void {
    // ブラウザバック対応。メッセージ送信後にブラウザバックされた場合にstoreに内容がないため表示する内容が無い
    // storeがリセットされていたら入力画面へ遷移させる
    if (this.state.exist == false) {
      this.$router.push(MobileUrl.SafetyConfirmOtherUserTempleteInput);
    }

    this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public createView(): void {
    this.isBusy = true;

    this.groupTargets = this.state.groupTargets ? this.state.groupTargets : [];
    this.openingSentence = this.state.openingSentence ? this.state.openingSentence : "";
    this.confirms = this.state.confirms;

    this.isBusy = false;

    return;
  }

  /**
   * ### 更新ボタン押下イベント
   *
   * - 安否確認事前設定(施設関係者)を更新する
   * - 安否確認事前設定(施設関係者)完了画面へ遷移する
   */
  public async onUpdate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中";

    try {
      const requestBody: OtherUserSafetyConfirmsTemplatesPatchModel = this.createRequestBody();
      await this.otherUserSafetyConfirmsTemplatesService.patch(userStore, 1, requestBody);

      mobileSafetyConfirmOtherUserTemplateStore.resetMobileSafetyConfirmOtherUserTemplateState();
      this.$router.push(MobileUrl.SafetyConfirmOtherUserTempleteComplete);
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 安否確認事前設定(施設関係者)更新リクエストボディを作成する
   *
   * - useIdsは「使用する」の質問内容のみにする
   */
  public createRequestBody(): OtherUserSafetyConfirmsTemplatesPatchModel {
    let useIds: Array<number> = [];
    for (const useId of this.confirms) {
      if (useId.use == true) {
        useIds.push(useId.id);
      }
    }

    const targets: string[] = [];
    for (const groupTarget of this.groupTargets) {
      switch (groupTarget.type) {
        case TargetType.SEND_MESSAGE_GROUP_FOR_OTHER_USER: {
          for (const target of groupTarget.targets) {
            const _target = target as SafetyConfirmTargetMessageGroupModel;
            if (target.isTarget) {
              targets.push(String(_target.id));
            }
          }
          break;
        }
        case TargetType.OTHER_USER_GROUP: {
          for (const target of groupTarget.targets) {
            const _target = target as SafetyConfirmTargetOtherUserGroupModel;
            if (target.isTarget) {
              targets.push(String(_target.otherUserGroupsSortKey));
            }
          }
          break;
        }
      }
    }

    const requestBody: OtherUserSafetyConfirmsTemplatesPatchModel = {
      useIds: useIds,
      targets: targets,
      openingSentence: this.openingSentence ? this.openingSentence : null,
      updated: this.state.updated as number
    };

    return requestBody;
  }

  /**
   * ### 戻るボタン押下イベント
   *
   * - 安否確認 登録情報 入力画面へ遷移する
   */
  public onCancel(): void {
    this.$router.push(MobileUrl.SafetyConfirmOtherUserTempleteInput);
    return;
  }

  /**
   * ### 質問事項の項目名を結合した表示用ラベルを作成する
   */
  public createQuestionsLabel(confirms: Array<UseId>): string {
    let labels: string[] = [];

    for (const confirm of confirms) {
      labels.push(confirm.label);
    }

    return labels.join("、");
  }
}
</script>

<style lang="scss" scopeed>
#other-user-template-confirm {
  #group-name,
  #opening-sentence,
  #questions {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  .register-btn,
  .back-btn {
    min-width: 84px;
  }
}
</style>
